import { RegionId } from "@sp-crm/core";
import { useState } from "react";
import { AgendaSnooze } from "./agenda-snoozed-section";
import {
    AgendaFeed,
    AgendaOrgCardState,
    agendaOrgGroups,
    mergeAgendaOrgCards,
} from "./marketing-plan-agenda";

interface MergedOrgAgenda {
    regionId: RegionId | null;
    feed: AgendaFeed | null;
    snoozes: AgendaSnooze[] | undefined;
    cards: AgendaOrgCardState[];
}

const emptyOrgAgenda = (regionId: RegionId | null): MergedOrgAgenda => ({
    regionId,
    feed: null,
    snoozes: undefined,
    cards: [],
});

const nextOrgAgenda = (
    merged: MergedOrgAgenda,
    regionId: RegionId | null,
    feed: AgendaFeed | undefined,
    snoozes: AgendaSnooze[] | undefined,
): MergedOrgAgenda => {
    const current = merged.regionId === regionId ? merged : emptyOrgAgenda(regionId);
    if (!feed || (feed === current.feed && snoozes === current.snoozes)) {
        return current;
    }
    return {
        regionId,
        feed,
        snoozes,
        cards: mergeAgendaOrgCards(current.cards, {
            groups: agendaOrgGroups(feed.orgs),
            snoozedBusinessIds: (snoozes ?? [])
                .filter(snooze => snooze.kind === "business")
                .map(snooze => snooze.businessId),
            completedBusinessIds: feed.completedBusinessIds,
        }),
    };
};

export const useAgendaOrgCards = (
    regionId: RegionId | null,
    feed: AgendaFeed | undefined,
    snoozes: AgendaSnooze[] | undefined,
): AgendaOrgCardState[] => {
    const [merged, setMerged] = useState<MergedOrgAgenda>(() => emptyOrgAgenda(regionId));
    const current = nextOrgAgenda(merged, regionId, feed, snoozes);
    if (current !== merged) {
        setMerged(current);
    }
    return current.cards;
};
